import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { matte, colored } from '../../Style.js';
import { SEA, foamRing, foamMesh } from './style.js';

// A floating supply buoy: a red-and-white float carrying a lashed medical
// crate with red crosses, bobbing and turning on the swell, ringed by foam
// and a soft green halo so it is easy to spot.
export class HealthPack {
  constructor(scene, healthPack) {
    this.scene = scene;
    this.healthPack = healthPack;
    this.mesh = null;
    this.buoy = null;
    this.ownMaterials = [];
    this.bobOffset = Math.random() * Math.PI * 2; // Random starting phase for bobbing
    this.create();
  }

  own(material) {
    this.ownMaterials.push(material);
    return material;
  }

  create() {
    this.mesh = new THREE.Group();
    const groundY = -this.healthPack.position.y;
    this.groundY = groundY;

    // The buoy rides on the water surface, whatever height the pickup reports
    this.buoy = new THREE.Group();
    this.buoy.position.y = groundY;
    this.mesh.add(this.buoy);

    const parts = [
      colored(new THREE.CylinderGeometry(0.75, 0.55, 0.5, 10), SEA.red, { y: 0.1 }),
      colored(new THREE.CylinderGeometry(0.77, 0.77, 0.14, 10), SEA.white, { y: 0.32 }),
      colored(new THREE.CylinderGeometry(0.6, 0.75, 0.12, 10), SEA.red, { y: 0.45 }),
      // Lashed crate
      colored(new THREE.BoxGeometry(0.85, 0.65, 0.85), SEA.white, { y: 0.84 }),
      colored(new THREE.BoxGeometry(0.89, 0.08, 0.89), SEA.woodDark, { y: 0.56 }),
      colored(new THREE.BoxGeometry(0.89, 0.08, 0.89), SEA.woodDark, { y: 1.12 }),
      // Mast with a little pennant and a lamp
      colored(new THREE.CylinderGeometry(0.04, 0.05, 1.1, 5), SEA.iron, { y: 1.7, x: 0.3, z: 0.3 }),
      colored(new THREE.ConeGeometry(0.12, 0.4, 3), SEA.red, { y: 2.1, x: 0.42, z: 0.3, rz: -Math.PI / 2, scale: [1, 1, 0.25] })
    ];
    // Red crosses on all four sides and the lid
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const face = 0.435;
      parts.push(colored(new THREE.BoxGeometry(0.42, 0.13, 0.02), SEA.red, { y: 0.84, z: face }).rotateY(a));
      parts.push(colored(new THREE.BoxGeometry(0.13, 0.42, 0.02), SEA.red, { y: 0.84, z: face }).rotateY(a));
    }
    parts.push(colored(new THREE.BoxGeometry(0.42, 0.02, 0.13), SEA.red, { y: 1.17 }));
    parts.push(colored(new THREE.BoxGeometry(0.13, 0.02, 0.42), SEA.red, { y: 1.17 }));
    const body = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    body.castShadow = true;
    this.buoy.add(body);

    this.lamp = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), this.own(new THREE.MeshBasicMaterial({ color: 0x9dffb0, toneMapped: false })));
    this.lamp.position.set(0.3, 2.3, 0.3);
    this.buoy.add(this.lamp);

    // Foam round the float and a pale green ring on the water to make it pop
    this.mesh.add(foamMesh(foamRing(0.8, 0.9, 20), groundY + 0.05));
    this.ring = new THREE.Mesh(new THREE.RingGeometry(1.5, 1.75, 32), this.own(new THREE.MeshBasicMaterial({
      color: 0x7dffb0, transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide
    })));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = groundY + 0.07;
    this.mesh.add(this.ring);

    this.mesh.position.set(
      this.healthPack.position.x,
      this.healthPack.position.y,
      this.healthPack.position.z
    );

    this.scene.add(this.mesh);
  }

  // Update animation (bobbing, rolling and pulsing)
  update(time) {
    if (!this.buoy) return;
    const t = time + this.bobOffset;
    this.buoy.position.y = this.groundY + Math.sin(t * 1.8) * 0.1;
    this.buoy.rotation.set(Math.sin(t * 1.3) * 0.08, time * 0.4, Math.cos(t * 1.1) * 0.08);
    const pulse = Math.sin(t * 3) * 0.5 + 0.5;
    this.ring.scale.setScalar(0.92 + pulse * 0.16);
    this.ring.material.opacity = 0.35 + pulse * 0.35;
    this.lamp.visible = (t % 1.6) < 0.9;
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      const geometries = new Set();
      this.mesh.traverse(child => {
        if (child.isMesh) geometries.add(child.geometry);
      });
      geometries.forEach(geometry => geometry.dispose());
      this.ownMaterials.forEach(material => material.dispose());
      this.ownMaterials = [];
      this.mesh = null;
      this.buoy = null;
    }
  }
}
